<template>
  <div class="footer">
    <v-bottom-navigation class="nav-bottom" app grow color="primary">
      <v-btn
        class="btn-nav"
        v-for="[icon, text, link, badge] in mainLinks"
        :key="icon"
        :to="link !== 'more' ? { name: link } : undefined"
        @click="link === 'more' ? toggleMore() : null"
        :class="[
          $vuetify.breakpoint.xsOnly ? 'btn-nav-small' : '',
          isActive(link) ? 'v-btn--active' : $route.meta.section !== 'dashboard' ? 'not-active' : '',
          `nav-btn--${link}`,
        ]"
        :ripple="false"
      >
        <div
          class="pt-1 text-uppercase font-weight-bold"
          :class="timeClockDuration && $route.meta.section !== 'timeclock' && link === 'timeclock' ? 'clock' : ''"
        >
          {{ text }}
        </div>
        <v-icon>{{ icon }}</v-icon>
        <v-badge v-if="badge" dot color="red" offset-x="-8" offset-y="6"></v-badge>
      </v-btn>
    </v-bottom-navigation>

    <v-bottom-sheet v-model="showMore" swipeable>
      <v-sheet
        class="text-center"
        height="450px"
        @touchstart="handleTouchStart"
        @touchmove="handleTouchMove"
        @touchend="handleTouchEnd"
      >
        <v-list>
          <div class="more-sheet-header" @click="showMore = false">
            <div class="handle-bar"></div>
            <!-- <v-btn icon small class="close-button" @click="showMore = false">
              <v-icon>mdi-close-thick</v-icon>
            </v-btn> -->
          </div>

          <v-list-item-group>
            <v-list-item
              v-for="(item, idx) in moreLinks"
              :key="idx"
              :href="item.href"
              :rel="item.href && item.href !== '#' ? 'noopener' : undefined"
              :target="item.href && item.href !== '#' ? '_blank' : undefined"
              :to="item.to"
              @click="showMore = false"
              :disabled="item.disabled"
              link
            >
              <v-list-item-icon>
                <v-icon :class="item.iconClass" :color="item.iconColor">{{ item.icon }}</v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title :class="item.labelClass">
                  {{ item.label }}
                </v-list-item-title>
              </v-list-item-content>

              <v-list-item-action>
                <v-icon v-if="item.newTab" color="grey">mdi-open-in-new</v-icon>
                <v-icon v-else color="grey lighten-1">mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-sheet>
    </v-bottom-sheet>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { TokenService } from "@services";

export default {
  name: "TheFooter",
  data: () => ({
    showMore: false,
    touchStart: null,
    touchY: null,
    isDragging: false,
  }),
  computed: {
    ...mapGetters("timeClock", ["timeClockDuration"]),
    ...mapGetters("notifications", ["notifications"]),

    mainLinks() {
      return [
        ["mdi-view-dashboard-outline", this.$t("c_menu.t_home"), "dashboard", null],
        [
          "mdi-clock-time-three-outline",
          this.timeClockDuration && this.$route.meta.section !== "timeclock"
            ? this.timeClockDuration
            : this.$t("c_menu.t_timeclock"),
          "timeclock",
          null,
        ],
        ["mdi-camera-outline", this.$t("c_menu.t_log_it"), "issues", null],
        ["mdi-form-select", this.$t("c_menu.t_forms"), "forms", null],
        ["mdi-menu", this.$t("c_menu.t_more"), "more", null],
      ].filter(Boolean);
    },
    moreLinks() {
      return [
        {
          icon: "mdi-cube-send",
          label: this.$t("c_menu_full.t_messages"),
          to: "/messages",
          routeSection: "messages",
          iconClass: "text-messages",
          labelClass: "more-nav-text text-left text-uppercase font-weight-bold"
        },
        {
          icon: "mdi-calendar-check",
          label: this.$t("c_menu_full.t_planners"),
          to: "/planners",
          routeSection: "planners",
          iconClass: "text-planners",
          labelClass: "more-nav-text text-left text-uppercase font-weight-bold"
        },
        {
          icon: "mdi-note-multiple-outline",
          label: this.$t("c_menu_full.t_documents"),
          to: "/documents",
          routeSection: "documents",
          iconClass: "text-documents",
          labelClass: "more-nav-text text-left text-uppercase font-weight-bold"
        },
        {
          icon: "mdi-school-outline",
          label: this.$t("c_menu_full.t_training"),
          to: "/training",
          routeSection: "training",
          iconClass: "text-training",
          labelClass: "more-nav-text text-left text-uppercase font-weight-bold"
        },
        {
          icon: "mdi-podcast",
          label: "ranesCAST",
          newTab: true,
          href: window.appConfig?.VUE_APP_CAST_WEB_URL
            ? window.appConfig?.VUE_APP_CAST_WEB_URL + "auth?source=app" + `&token=${TokenService.getRefreshToken()}`
            : undefined,
          disabled: !window.appConfig?.VUE_APP_CAST_WEB_URL,
          tooltip: !window.appConfig?.VUE_APP_CAST_WEB_URL ? "ranesCAST is temporarily unavailable" : undefined,
          iconColor: "#e54634",
          labelClass: "more-nav-text text-left font-weight-bold"
        },
        {
          icon: "mdi-shield-lock",
          label: "Admin",
          newTab: true,
          href: window.appConfig?.VUE_APP_PORTAL_URL,
          disabled: !window.appConfig?.VUE_APP_PORTAL_URL,
          tooltip: !window.appConfig?.VUE_APP_PORTAL_URL ? "The Admin Portal is temporarily unavailable" : undefined,
          iconColor: "#ff2255",
          labelClass: "more-nav-text text-left text-uppercase font-weight-bold admin-label"
        },
        {
          icon: "mdi-cog-outline",
          label: this.$t("c_menu_full.t_settings"),
          to: "/account",
          routeSection: "account",
          iconClass: "text-account",
          labelClass: "more-nav-text text-left text-uppercase font-weight-bold"
        }
      ]
    },
  },
  methods: {
    toggleMore() {
      this.showMore = !this.showMore;
    },
    isActive(link) {
      if (link === "more") {
        return false;
      }
      return this.$route.meta.section === link;
    },
    handleTouchStart(event) {
      this.touchStart = event.touches[0].clientY;
      this.isDragging = true;
    },
    handleTouchMove(event) {
      if (!this.isDragging) return;

      this.touchY = event.touches[0].clientY;
      const deltaY = this.touchY - this.touchStart;

      if (deltaY > 0) {
        const bottomSheet = event.target.closest(".v-bottom-sheet");
        if (bottomSheet) {
          bottomSheet.style.transform = `translateY(${deltaY}px)`;
          bottomSheet.style.transition = "none";
        }
      }
    },
    handleTouchEnd(event) {
      if (!this.isDragging) return;

      const deltaY = this.touchY - this.touchStart;
      const bottomSheet = event.target.closest(".v-bottom-sheet");

      if (deltaY > 100) {
        this.showMore = false;
      }

      if (bottomSheet) {
        bottomSheet.style.transform = "";
        bottomSheet.style.transition = "transform 0.3s";
      }

      this.isDragging = false;
      this.touchStart = null;
      this.touchY = null;
    },
  },
};
</script>

<style lang="scss">
@import "@/scss/variables";

.v-btn--active.no-active:not(:hover)::before {
  opacity: 0 !important;
  background-color: transparent !important;
}
.v-btn--active::before {
  background-color: transparent !important;
}
.v-item-group.v-bottom-navigation .v-btn {
  min-width: 65px;
}
.theme--dark.v-bottom-navigation .v-btn.not-active.v-btn--active {
  color: rgba(255, 255, 255, 0.7) !important;
}
.theme--light.v-bottom-navigation .v-btn.not-active.v-btn--active {
  color: rgba(0, 0, 0, 0.6) !important;
}

// Apply section colors to footer navigation icons
.nav-btn--dashboard .v-icon {
  color: $color-primary !important;
}
.nav-btn--timeclock .v-icon {
  color: $color-timeclock !important;
}
.nav-btn--issues .v-icon {
  color: $color-issues !important;
}
.nav-btn--forms .v-icon {
  color: $color-forms !important;
}
.nav-btn--more .v-icon {
  color: $color-gray-600 !important;
}

.v-bottom-sheet {
  border-top: 3px solid #f44336;
  margin-bottom: calc(56px + env(safe-area-inset-bottom, 0px)) !important;
  z-index: 9999;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  .v-list-item-group {
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    margin-top: 8px;
    &.theme--dark {
      border-top: 1px solid rgba(255, 255, 255, 0.12);
      border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    }
  }

  .v-list-item {
    background: rgba(0, 0, 0, 0.03);
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);

    &:last-child {
      border-bottom: none;
    }
    &.theme--dark {
      background: rgba(255, 255, 255, 0.02);
      border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    }
    .more-nav-text {
      font-family: "montserrat", sans-serif !important;
      font-size: 0.875rem !important;
      letter-spacing: 0.0892857143em;
    }
  }

  .more-sheet-header {
    position: relative;
    padding: 8px;
    .handle-bar {
      width: 36px;
      height: 4px;
      border-radius: 2px;
      background-color: rgba(0, 0, 0, 0.2);
      margin: 0 auto;
      cursor: pointer;

      &:hover {
        background-color: rgba(0, 0, 0, 0.3);
      }
    }

    .close-button {
      position: absolute;
      right: 16px;
      top: 1px;
    }
  }

  // Dark theme support
  .theme--dark {
    .handle-bar {
      background-color: rgba(255, 255, 255, 0.2);

      &:hover {
        background-color: rgba(255, 255, 255, 0.3);
      }
    }
  }
}

.footer {
  .nav-bottom {
    border-top: 1px solid #ccc;
    height: auto !important;
    z-index: 9999;
    position: fixed !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    .clock {
      margin: 0 auto;
    }
    .btn-nav {
      padding-top: 7px !important;
      padding-bottom: 10px;
    }
    .btn-nav-small {
      font-size: 10px !important;
      font-family: "roboto", sans-serif !important;
      min-width: 50px !important;
    }
    .v-btn:before {
      background-color: transparent;
    }
    @media (max-width: 449px) {
      .btn-nav-small {
        font-size: 9px !important;
      }
    }
  }
}

/* Ensure bottom navigation stays above other content */
.v-bottom-navigation {
  z-index: 9999 !important;
}

/* Handle iOS safe areas */
@supports (padding: max(0px)) {
  .nav-bottom {
    padding-bottom: env(safe-area-inset-bottom) !important;
  }
}
</style>
